import { useEffect } from 'react'
import { STATE_RESPONSES } from 'components/survey/question-types/custom/insurance-covered-loading'

const STATE_QUESTION_ID = 202503192

/**
 * Maps state query param value to response ID for the state question
 * @param stateValue - State value from query params (can be state abbreviation like "CO" or response ID like "1141")
 * @returns Response ID as number, or null if not found
 */
function getStateResponseId(
  stateValue: string | string[] | undefined
): number | null {
  if (!stateValue) return null

  const stateStr = Array.isArray(stateValue) ? stateValue[0] : stateValue

  // If it's already a numeric response ID, return it
  const numericValue = Number(stateStr)
  if (!isNaN(numericValue) && STATE_RESPONSES[numericValue]) {
    return numericValue
  }

  // Otherwise, treat it as a state abbreviation and find the response ID
  // STATE_RESPONSES maps responseId -> stateAbbr, so we need to reverse lookup
  const responseId = Object.keys(STATE_RESPONSES).find(
    key => STATE_RESPONSES[Number(key)] === stateStr.toUpperCase()
  )

  return responseId ? Number(responseId) : null
}

interface UsePrepopulateStateProps {
  currentQuestionId: string | number
  hasAnswer: boolean
  setAnswer: (questionId: string, value: number) => void
  routerQuery?: Record<string, string | string[] | undefined>
}

export function usePrepopulateState({
  currentQuestionId,
  hasAnswer,
  setAnswer,
  routerQuery,
}: UsePrepopulateStateProps) {
  useEffect(() => {
    const stateQuestionIdStr = String(STATE_QUESTION_ID)

    // Only pre-populate if current question is the state question
    if (String(currentQuestionId) !== stateQuestionIdStr) {
      return
    }

    // Precedence 1: If answer is already answered, skip
    if (hasAnswer) {
      return
    }

    // Precedence 2: If there is query param state, use that
    if (routerQuery?.state) {
      const responseId = getStateResponseId(routerQuery.state)
      if (responseId) {
        setAnswer(stateQuestionIdStr, responseId)
        return
      }
    }

    // Precedence 3: Use Vercel geo cookie (set by middleware from visitor region)
    const geoRegion =
      typeof document !== 'undefined'
        ? document.cookie
            .split('; ')
            .find(c => c.startsWith('geo-region='))
            ?.split('=')[1]
        : undefined

    if (geoRegion) {
      const responseId = Object.keys(STATE_RESPONSES).find(
        key => STATE_RESPONSES[Number(key)] === geoRegion
      )
      if (responseId) {
        setAnswer(stateQuestionIdStr, Number(responseId))
      }
    }
  }, [currentQuestionId, hasAnswer, setAnswer, routerQuery])
}
